import {
  defaultShouldDehydrateQuery,
  isServer,
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";

import { getQueryRetryDelay, shouldRetryQuery } from "@/lib/trpc/edge-errors";

function getTwoFactorRedirectUrl(error: unknown): string | null {
  if (isServer) {
    return null;
  }
  if (!(error instanceof TRPCClientError)) {
    return null;
  }
  if (error.data?.code !== "FORBIDDEN") {
    return null;
  }
  // Already on the 2FA page; redirecting would create a loop. The page will
  // verify and navigate the user away on its own.
  if (window.location.pathname.startsWith("/two-factor-auth")) {
    return null;
  }
  const message = error.message.toLowerCase();
  const returnUrl = encodeURIComponent(window.location.pathname);
  if (message.includes("two-factor authentication required")) {
    return `/two-factor-auth?mode=verify&returnUrl=${returnUrl}`;
  }
  if (message.includes("requires two-factor authentication")) {
    return `/two-factor-auth?mode=setup&returnUrl=${returnUrl}`;
  }
  return null;
}

function handleGlobalError(error: unknown) {
  const redirectUrl = getTwoFactorRedirectUrl(error);
  if (redirectUrl) {
    window.location.href = redirectUrl;
  }
}

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: handleGlobalError,
    }),
    mutationCache: new MutationCache({
      onError: handleGlobalError,
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 min - fresh data won't refetch on mount
        gcTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        refetchOnMount: true, // this won't refetch if the data is fresh, this only happens if the data is stale
        refetchIntervalInBackground: false,
        // Back off on 429s and stop retrying deterministic 4xx entirely.
        // TanStack's default (3 immediate retries on every failure) turned a
        // brief edge rate-limit into a self-sustaining request storm.
        retry: shouldRetryQuery,
        retryDelay: getQueryRetryDelay,
      },
      dehydrate: {
        // Include pending queries in dehydration for streaming SSR
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
        // Keep default error redaction to prevent leaking server internals to browser
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export default function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: reuse the same client to prevent re-creation during React suspense
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}
